import { useMutation } from '@apollo/client';
import { Add, ClearAll, LibraryAdd, PlaylistAdd } from '@mui/icons-material';
import { IconButton, Skeleton, TextField, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import { alpha, type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useFormik } from 'formik';
import type React from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import assetsByCollectionId from 'src/api/assets/assetsByCollectionId';
import createCollectionList from 'src/api/collection/createCollectionList';
import showMyCollections from 'src/api/collection/showMyCollections';
import ConfirmDialog from 'src/components/dialogs/ConfirmDialog';
import CreatePlayListContext from 'src/context/CreatePlayListContext';
import usePlaylistItemStyles from 'src/HomePage/DrawerAndPushedContents/RightSideDrawer/playlistItemStyles';
import viewStateVar from 'src/store/viewState';
import * as Yup from 'yup';
import CollectionItem from './NewCollectionItem';

const useStyles = makeStyles((theme: Theme) => ({
  '@keyframes subtlePulse': {
    '0%': { borderColor: alpha(theme.palette.secondary.main, 0.19) },
    '50%': { borderColor: alpha(theme.palette.secondary.main, 0.38) },
    '100%': { borderColor: alpha(theme.palette.secondary.main, 0.19) },
  },
  newCollectionForm: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  dropZone: {
    flex: 1,
    minHeight: 60,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    backgroundColor: alpha(theme.palette.secondary.main, 0.03),
    border: `2px dashed ${alpha(theme.palette.secondary.main, 0.19)}`,
    borderRadius: 8,
    margin: '0 8px',
    width: 'calc(100% - 16px)',
    animation: '$subtlePulse 3s ease-in-out infinite',
    transition: 'background-color 200ms, border-color 200ms, box-shadow 200ms',
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.08),
      borderColor: alpha(theme.palette.secondary.main, 0.38),
    },
  },
  dropZoneOver: {
    backgroundColor: alpha(theme.palette.secondary.main, 0.15),
    borderColor: alpha(theme.palette.secondary.main, 0.56),
    borderStyle: 'solid',
    boxShadow: `inset 0 0 20px ${alpha(theme.palette.secondary.main, 0.12)}`,
    animation: 'none',
  },
  dropZoneIcon: {
    fontSize: 40,
    color: alpha(theme.palette.secondary.main, 0.31),
    transition: 'color 200ms, transform 200ms',
  },
  dropZoneIconOver: {
    color: theme.palette.secondary.main,
    transform: 'scale(1.15)',
  },
  dropZoneText: {
    fontSize: 13,
    fontWeight: 500,
    color: alpha(theme.palette.secondary.main, 0.5),
    letterSpacing: '0.3px',
    transition: 'color 200ms',
  },
  dropZoneTextOver: {
    color: theme.palette.secondary.main,
    fontWeight: 600,
  },
  dropZoneSubtext: {
    fontSize: 10,
    color: theme.palette.text.disabled,
    marginTop: -4,
  },
  dropZoneWithItems: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  dropPadding: {
    minHeight: 40,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
    borderTop: `1px dashed ${alpha(theme.palette.secondary.main, 0.15)}`,
    transition: 'background-color 200ms',
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.06),
    },
  },
  dropPaddingOver: {
    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
    borderTopColor: alpha(theme.palette.secondary.main, 0.38),
  },
  listPaper: {
    overflow: 'auto',
    maxHeight: 'calc(100vh - 280px)',
    borderTop: '2px solid',
    borderImage: `linear-gradient(90deg, ${alpha(theme.palette.secondary.main, 1)}, transparent) 1`,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    backgroundImage: 'none',
  },
  form: {
    display: 'flex',
    gap: theme.spacing(0.75),
    alignItems: 'center',
    padding: `${theme.spacing(0.75)} 12px`,
    flexShrink: 0,
  },
  formInput: {
    '& .MuiOutlinedInput-root': {
      fontSize: 12,
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 10px',
    },
  },
  dropHint: {
    fontSize: 11,
    color: theme.palette.secondary.main,
  },
}));

interface FormValues {
  name: string;
}

const NewCollectionDetails: React.FC = () => {
  const classes = useStyles();
  const { createAddedAssets, setCreateAddedAssets } = useContext(CreatePlayListContext);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [flashItemId, setFlashItemId] = useState<string | null>(null);
  const [showPendingSkeleton, setShowPendingSkeleton] = useState(false);
  const prevCountRef = useRef(createAddedAssets.length);
  const listRef = useRef<HTMLDivElement>(null);

  const [createCollection, { loading }] = useMutation(createCollectionList, {
    fetchPolicy: 'network-only',
  });

  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'CARD_AKOMI',
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    drop: () => {
      setShowPendingSkeleton(true);
    },
  }));

  useEffect(() => {
    if (createAddedAssets.length > prevCountRef.current) {
      setShowPendingSkeleton(false);
      const newItem = createAddedAssets[createAddedAssets.length - 1];
      if (newItem) {
        setFlashItemId(newItem.id);
        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
          }
        }, 50);
        setTimeout(() => setFlashItemId(null), 1500);
      }
    }
    prevCountRef.current = createAddedAssets.length;
  }, [createAddedAssets]);

  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('This field is required'),
    }),
    onSubmit: async (values) => {
      const { name } = values;
      createCollection({
        variables: {
          name,
          asset_ids: createAddedAssets.map((asset) => asset.id),
          collection_type: 'PLAYLIST',
        },
        refetchQueries: [assetsByCollectionId, showMyCollections],
      })
        .then(() => {
          console.log('collection created');
        })
        .catch(() => {
          console.log('An error occured while creating collection');
        });
      viewStateVar({
        ...viewStateVar(),
        openNewCollection: false,
        openEditCollection: false,
        openMyCollection: true,
      });
      setCreateAddedAssets([]);
      formik.resetForm();
    },
  });

  const hoverCard = useCallback(
    (fromIndex: number, toIndex: number): void => {
      setCreateAddedAssets((items) => {
        const newItems = [...items];
        const [moved] = newItems.splice(fromIndex, 1);
        newItems.splice(toIndex, 0, moved);
        return newItems;
      });
    },
    [setCreateAddedAssets],
  );

  const moveCard = useCallback((_id: string, _toIndex: number): void => {
    // State already updated by hoverCard — no server call needed for new playlists
  }, []);

  const sharedClasses = usePlaylistItemStyles();

  if (loading)
    return (
      <div className={classes.newCollectionForm}>
        {[0, 1].map((i) => (
          <div key={i} className={sharedClasses.skeletonRow}>
            <Skeleton variant="rectangular" width={32} height={22} animation="wave" sx={{ borderRadius: 0.5 }} />
            <Skeleton variant="text" width="70%" height={16} animation="wave" />
          </div>
        ))}
      </div>
    );

  return (
    <div
      ref={(node) => {
        drop(node);
      }}
      className={`${classes.newCollectionForm} ${isOver && createAddedAssets.length === 0 ? classes.dropZoneOver : ''}`}
    >
      <form className={classes.form} onSubmit={formik.handleSubmit}>
        <TextField
          id="cyp-create-playlist-input"
          className={classes.formInput}
          placeholder="Playlist name"
          variant="outlined"
          size="small"
          fullWidth
          {...formik.getFieldProps('name')}
        />
        <IconButton id="cyp-create-playlist-btn" color="secondary" type="submit" size="small">
          <Add sx={{ fontSize: 16 }} />
        </IconButton>
        {createAddedAssets.length > 0 && (
          <IconButton
            size="small"
            onClick={() => setShowClearConfirm(true)}
            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
          >
            <ClearAll sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </form>
      <div
        className={`${createAddedAssets.length > 0 ? classes.dropZoneWithItems : `${classes.dropZone} ${isOver ? classes.dropZoneOver : ''}`} ${createAddedAssets.length > 0 && isOver ? sharedClasses.dropZoneActive : ''}`}
      >
        {createAddedAssets.length === 0 && !showPendingSkeleton ? (
          <>
            {isOver ? (
              <LibraryAdd className={`${classes.dropZoneIcon} ${classes.dropZoneIconOver}`} />
            ) : (
              <PlaylistAdd className={classes.dropZoneIcon} />
            )}
            <Typography className={`${classes.dropZoneText} ${isOver ? classes.dropZoneTextOver : ''}`}>
              {isOver ? 'Release to add' : 'Drag media here to add'}
            </Typography>
            {!isOver && (
              <Typography className={classes.dropZoneSubtext}>or use the + menu on any asset card</Typography>
            )}
          </>
        ) : (
          <>
            <Paper ref={listRef} className={`${classes.listPaper} ${sharedClasses.thinScrollbar}`}>
              {createAddedAssets.map((asset, index) => (
                <CollectionItem
                  key={asset.id}
                  id={asset.id}
                  name={asset.name}
                  mediatype={asset.mediatype}
                  index={index}
                  hoverCard={hoverCard}
                  moveCard={moveCard}
                  flashId={flashItemId}
                />
              ))}
              {showPendingSkeleton && (
                <div className={sharedClasses.pendingSkeleton}>
                  <Skeleton
                    variant="rectangular"
                    width={36}
                    height={24}
                    animation="wave"
                    sx={{ borderRadius: 0.5, flexShrink: 0 }}
                  />
                  <Skeleton variant="text" width="60%" height={16} animation="wave" sx={{ flex: 1 }} />
                </div>
              )}
            </Paper>
            <div className={`${classes.dropPadding} ${isOver ? classes.dropPaddingOver : ''}`}>
              <PlaylistAdd sx={{ fontSize: 14, color: isOver ? 'secondary.main' : 'text.disabled' }} />
              <Typography className={classes.dropHint}>{isOver ? 'Release to add' : 'Drop more'}</Typography>
            </div>
          </>
        )}
      </div>
      <ConfirmDialog
        isOpen={showClearConfirm}
        title="Clear playlist"
        message="Remove all items from this playlist? This cannot be undone."
        onConfirm={() => {
          setCreateAddedAssets([]);
          setShowClearConfirm(false);
        }}
        onCancel={() => setShowClearConfirm(false)}
      />
    </div>
  );
};

export default NewCollectionDetails;
